import { createReducer, on } from '@ngrx/store';
import { ResourcesActions } from '../actions';
import {
  IAreaDetail,
  IPropertyFilter,
  IResourceFilterUpdateCrate,
  IResourceListItem,
  Requirement,
} from '@outlook-addin/cue-http';
import { NavigationDetailInfo } from '@cue/navigation-services';
import { Pack } from '@cue/data';

export type State = {
  areas: IAreaDetail[];
  resourcesFilter: IPropertyFilter[];
  resourceList: Pack<IResourceListItem[]>;
  resourceDetail: Pack<NavigationDetailInfo>;
  currentResourceListFilter: IResourceFilterUpdateCrate;
  defaultLanguage: string;
  requirements: Requirement[];
  virtualRequirements: {
    filter: IPropertyFilter;
    id: string;
  }[];
};

export const initialState: State = {
  areas: [],
  resourcesFilter: null,
  defaultLanguage: 'en',
  requirements: [],
  virtualRequirements: [],
  resourceList: {
    data: [],
    loading: false,
  },
  resourceDetail: {
    data: null,
    loading: false,
  },
  currentResourceListFilter: {
    start: null,
    end: null,
    additionalFilterList: [],
    resourceName: null,
    iCalUid: null,
    currentEventId: null,
    currentEventResourceEmails: [],
    currentEventStartLocalDateTime: null,
    currentEventEndLocalDateTime: null,
    currentEventStartTimeZone: null,
    currentEventEndTimeZone: null,
    showOnlyAvailable: localStorage.getItem('showOnlyAvailableFilter')
      ? localStorage.getItem('showOnlyAvailableFilter') === 'true'
      : null,
  },
};

export const reducer = createReducer(
  initialState,
  on(ResourcesActions.setDefaultLanguage, (state, { defaultLanguage }) => ({
    ...state,
    defaultLanguage,
  })),
  on(ResourcesActions.loadAreas, (state) => ({
    ...state,
    areas: [],
  })),
  on(ResourcesActions.areasLoaded, (state, { areas }) => ({
    ...state,
    areas: areas,
  })),
  on(ResourcesActions.loadResourcesFilter, (state) => ({
    ...state,
    resourcesFilter: [],
  })),
  on(ResourcesActions.resourcesFilterLoaded, (state, action) => ({
    ...state,
    resourcesFilter: action.filter,
    virtualRequirements:
      state.requirements.length === 0
        ? action.filter
            .filter((x) => x.resourceCount > 0)
            .map((f) => ({
              filter: f,
              id: crypto.randomUUID(),
            }))
        : [],
  })),
  on(ResourcesActions.loadResourceList, (state) => ({
    ...state,
    resourceList: {
      data: state.resourceList?.data,
      loading: true,
    },
  })),
  on(ResourcesActions.loadResourceListInBackground, (state) => ({
    ...state,
    resourceList: {
      data: state.resourceList?.data,
      loading: false,
    },
  })),
  on(ResourcesActions.resourceListLoadSuccess, (state, action) => ({
    ...state,
    resourceList: {
      data: action.resourceList,
      loading: false,
    },
    isResourceListLoading: false,
  })),
  on(ResourcesActions.resourceListLoadFailed, (state, action) => ({
    ...state,
    resourceList: {
      data: [],
      loading: false,
      error: action.error,
    },
    isResourceListLoading: false,
  })),
  on(ResourcesActions.resourceListBackgroundLoadSuccess, (state, action) => ({
    ...state,
    resourceList: {
      data: action.resourceList,
      loading: state.resourceList.loading,
    },
  })),
  on(ResourcesActions.resourceListBackgroundLoadFailed, (state) => ({
    ...state,
    resourceList: {
      ...state.resourceList,
      loading: state.resourceList.loading,
    },
  })),
  on(ResourcesActions.loadResourceDetail, (state) => ({
    ...state,
    resourceDetail: {
      data: null,
      loading: true,
    },
  })),
  on(ResourcesActions.resourceDetailLoadSuccess, (state, action) => ({
    ...state,
    resourceDetail: {
      data: action.resourceDetail,
      loading: false,
      error: null,
    },
  })),
  on(ResourcesActions.resourceDetailLoadFailed, (state, action) => ({
    ...state,
    resourceDetail: {
      data: null,
      loading: false,
      error: action.error,
    },
  })),
  on(ResourcesActions.updateStartEndFilter, (state, action) => ({
    ...state,
    currentResourceListFilter: {
      ...state.currentResourceListFilter,
      start: action.start,
      end: action.end,
      iCalUid: action.iCalUid !== undefined ? action.iCalUid : state.currentResourceListFilter.iCalUid,
      currentEventId:
        action.currentEventId !== undefined ? action.currentEventId : state.currentResourceListFilter.currentEventId,
      currentEventResourceEmails:
        action.currentEventResourceEmails ?? state.currentResourceListFilter.currentEventResourceEmails,
      currentEventStart:
        action.currentEventStart !== undefined
          ? action.currentEventStart
          : state.currentResourceListFilter.currentEventStart,
      currentEventEnd:
        action.currentEventEnd !== undefined ? action.currentEventEnd : state.currentResourceListFilter.currentEventEnd,
      currentEventStartLocalDateTime:
        action.currentEventStartLocalDateTime !== undefined
          ? action.currentEventStartLocalDateTime
          : state.currentResourceListFilter.currentEventStartLocalDateTime,
      currentEventEndLocalDateTime:
        action.currentEventEndLocalDateTime !== undefined
          ? action.currentEventEndLocalDateTime
          : state.currentResourceListFilter.currentEventEndLocalDateTime,
      currentEventStartTimeZone:
        action.currentEventStartTimeZone !== undefined
          ? action.currentEventStartTimeZone
          : state.currentResourceListFilter.currentEventStartTimeZone,
      currentEventEndTimeZone:
        action.currentEventEndTimeZone !== undefined
          ? action.currentEventEndTimeZone
          : state.currentResourceListFilter.currentEventEndTimeZone,
    },
  })),
  on(ResourcesActions.updateOnlyAvailableFilter, (state, action) => ({
    ...state,
    currentResourceListFilter: {
      ...state.currentResourceListFilter,
      showOnlyAvailable: action.onlyAvailable,
    },
  })),
  on(ResourcesActions.searchTextChanged, (state, action) => ({
    ...state,
    currentResourceListFilter: {
      ...state.currentResourceListFilter,
      resourceName: action.searchText,
    },
  })),

  on(ResourcesActions.resetRequirements, (state) => ({
    ...state,
    requirements: [],
    virtualRequirements: state.resourcesFilter
      .filter((x) => x.resourceCount > 0)
      .map((f) => ({
        filter: f,
        id: crypto.randomUUID(),
      })),
    currentResourceListFilter: {
      ...state.currentResourceListFilter,
      additionalFilterList: [],
    },
  })),
  on(ResourcesActions.updateResourceFilter, (state, action) => ({
    ...state,
    requirements: action.requirements,
    virtualRequirements:
      action.requirements.length === 0
        ? state.resourcesFilter
            .filter((x) => x.resourceCount > 0)
            .map((f) => ({
              filter: f,
              id: crypto.randomUUID(),
            }))
        : [],
    currentResourceListFilter: action.filterUpdate
      ? {
          showOnlyAvailable:
            action.filterUpdate?.showOnlyAvailable != null
              ? action.filterUpdate.showOnlyAvailable
              : state.currentResourceListFilter?.showOnlyAvailable,
          start:
            action.filterUpdate?.start != null ? action.filterUpdate.start : state.currentResourceListFilter?.start,
          end: action.filterUpdate?.end != null ? action.filterUpdate.end : state.currentResourceListFilter?.end,
          resourceName:
            action.filterUpdate?.resourceName != null
              ? action.filterUpdate.resourceName
              : state.currentResourceListFilter?.resourceName,
          iCalUid:
            action.filterUpdate?.iCalUid !== undefined
              ? action.filterUpdate.iCalUid
              : state.currentResourceListFilter?.iCalUid,
          currentEventId:
            action.filterUpdate?.currentEventId !== undefined
              ? action.filterUpdate.currentEventId
              : state.currentResourceListFilter?.currentEventId,
          currentEventResourceEmails:
            action.filterUpdate?.currentEventResourceEmails !== undefined
              ? action.filterUpdate.currentEventResourceEmails
              : state.currentResourceListFilter?.currentEventResourceEmails,
          currentEventStart:
            action.filterUpdate?.currentEventStart !== undefined
              ? action.filterUpdate.currentEventStart
              : state.currentResourceListFilter?.currentEventStart,
          currentEventEnd:
            action.filterUpdate?.currentEventEnd !== undefined
              ? action.filterUpdate.currentEventEnd
              : state.currentResourceListFilter?.currentEventEnd,
          currentEventStartLocalDateTime:
            action.filterUpdate?.currentEventStartLocalDateTime !== undefined
              ? action.filterUpdate.currentEventStartLocalDateTime
              : state.currentResourceListFilter?.currentEventStartLocalDateTime,
          currentEventEndLocalDateTime:
            action.filterUpdate?.currentEventEndLocalDateTime !== undefined
              ? action.filterUpdate.currentEventEndLocalDateTime
              : state.currentResourceListFilter?.currentEventEndLocalDateTime,
          currentEventStartTimeZone:
            action.filterUpdate?.currentEventStartTimeZone !== undefined
              ? action.filterUpdate.currentEventStartTimeZone
              : state.currentResourceListFilter?.currentEventStartTimeZone,
          currentEventEndTimeZone:
            action.filterUpdate?.currentEventEndTimeZone !== undefined
              ? action.filterUpdate.currentEventEndTimeZone
              : state.currentResourceListFilter?.currentEventEndTimeZone,
          additionalFilterList:
            action.filterUpdate?.additionalFilterList != null
              ? action.filterUpdate.additionalFilterList
              : state.currentResourceListFilter?.additionalFilterList,
        }
      : null,
  }))
);
